import { useEffect, useState } from 'react';

import { translate } from '../../../common/helpers/translate.helper';
import { Box } from '../../../common/ui/Box';
import { Button } from '../../Button/Button';
import { Label } from '../../Label/Label';
import { Radio, RadioGroup, RadioGroupItem } from '../../RadioGroup/RadioGroup';
import { Text } from '../../Text/Text';
import { FilterPill, FilterPillProps } from '../FilterPill/FilterPill';

type FilterRadioGroupValue = string | boolean;

interface FilterRadioGroupOption<T extends FilterRadioGroupValue> {
    label: string;
    value: T;
}

interface FilterRadioGroupProps<T extends FilterRadioGroupValue> extends Pick<FilterPillProps, 'label'> {
    filterByLabel: string;
    options: FilterRadioGroupOption<T>[];
    value?: T;
    onChange?: (value?: T) => void;
}

const getSelectedOptionLabel = <T extends FilterRadioGroupValue>(
    options: FilterRadioGroupOption<T>[],
    value?: T | null
): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    return options.find((option) => option.value === value)?.label;
};

const toRadioGroupValue = (value?: FilterRadioGroupValue | null): string | null => {
    if (value === undefined || value === null) {
        return null;
    }

    return String(value);
};

const fromRadioGroupValue = <T extends FilterRadioGroupValue>(
    options: FilterRadioGroupOption<T>[],
    selectedValue: string
): T | null => {
    return options.find((option) => String(option.value) === selectedValue)?.value ?? null;
};

export const FilterRadioGroup = <T extends FilterRadioGroupValue>({
    label,
    filterByLabel,
    options,
    value,
    onChange,
}: FilterRadioGroupProps<T>) => {
    const [draftValue, setDraftValue] = useState<T | null>(value ?? null);

    useEffect(() => {
        setDraftValue(value ?? null);
    }, [value]);

    return (
        <FilterPill
            label={label}
            value={getSelectedOptionLabel(options, draftValue)}
            onClear={() => {
                setDraftValue(null);
                onChange?.(undefined);
            }}
            onPopoverClose={() => {
                setDraftValue(value ?? null);
            }}
        >
            {(closePopover) => (
                <Box padding="12px" display="flex" flexDirection="column" gap={16}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={16}>
                        <Text variant="body" fontWeight={600}>
                            {filterByLabel}
                        </Text>
                        <Button
                            size="small"
                            emphasis="high"
                            onClick={() => {
                                onChange?.(draftValue ?? undefined);
                                closePopover();
                            }}
                        >
                            {translate('global.filterRadioGroup.apply')}
                        </Button>
                    </Box>
                    <RadioGroup
                        value={toRadioGroupValue(draftValue)}
                        onValueChange={(selectedValue) => setDraftValue(fromRadioGroupValue(options, selectedValue))}
                    >
                        {options.map((option) => (
                            <RadioGroupItem key={String(option.value)}>
                                <Radio value={String(option.value)} />
                                <Label>{option.label}</Label>
                            </RadioGroupItem>
                        ))}
                    </RadioGroup>
                </Box>
            )}
        </FilterPill>
    );
};
